import type { GattCharacteristic, GattServer } from '@/ble/types';
import { subscribe, writeChar } from '@/ble/gatt';
import { UUID } from '@/ble/uuids';
import { asBytes, toHex } from '@/ble/bytes';
import { WriteQueue } from '@/ble/writeQueue';
import type { AnyTrainer, SimParams } from '@/trainer/types';
import { BleTrainerBase } from '../bleTrainerBase';
import { ERG_MIN_INTERVAL_MS, SIM_MIN_INTERVAL_MS } from '../ftms/FtmsTrainer';
import {
  encodeTargetPower,
  encodeTrackResistance,
  encodeUserConfig,
  encodeWindResistance,
  FEC_PAGE,
  frame,
  parsePage,
  unframe,
} from './protocol';

/**
 * Tacx FE-C over BLE. Writes go to `…fec3`, data arrives on `…fec2` (PROTOCOLS.md §2).
 * FE-C has no per-command response on BLE that every trainer sends reliably, so the queue paces writes
 * by rate limit and releases on page 71 (command status) when it arrives, or after a short timeout.
 */
export class FecTrainer extends BleTrainerBase implements AnyTrainer {
  readonly kind = 'fec' as const;
  private tx: GattCharacteristic | null = null;
  private queue: WriteQueue | null = null;
  private user = { riderKg: 75, bikeKg: 9, wheelCircumferenceM: 2.105 };
  private lastCw: number | null = null;

  protected async setup(server: GattServer): Promise<void> {
    const svc = await server.getPrimaryService(UUID.FEC);
    const rx = await svc.getCharacteristic(UUID.FEC_NOTIFY);
    this.tx = await svc.getCharacteristic(UUID.FEC_WRITE);
    const tx = this.tx;
    this.queue = new WriteQueue({
      awaitResponse: true,
      responseTimeoutMs: 400,
      send: async (bytes) => {
        this.log('tx', bytes);
        await writeChar(tx, bytes, true);
      },
      onLog: (m) => this.log('--', null, m),
    });
    this.unsubs.push(await subscribe(rx, (dv) => this.onMessage(dv)));
    this.lastCw = null;
    void this.sendUserConfig();
  }

  protected onTransportLost(): void {
    this.queue?.clear();
    this.queue = null;
    this.tx = null;
  }

  async setUserConfig(cfg: { riderKg: number; bikeKg: number; wheelCircumferenceM: number }): Promise<void> {
    this.user = cfg;
    await this.sendUserConfig();
  }

  private async sendUserConfig(): Promise<void> {
    const diameter = this.user.wheelCircumferenceM / Math.PI;
    await this.queue?.enqueue(
      'user',
      frame(encodeUserConfig(this.user.riderKg, this.user.bikeKg, diameter)),
      {
        responseKey: FEC_PAGE.USER_CONFIG,
      },
    );
  }

  async setErg(watts: number): Promise<void> {
    await this.queue?.enqueue('erg', frame(encodeTargetPower(watts)), {
      responseKey: FEC_PAGE.TARGET_POWER,
      minIntervalMs: ERG_MIN_INTERVAL_MS,
    });
  }

  async setSim(p: SimParams): Promise<void> {
    const q = this.queue;
    if (!q) return;
    if (this.lastCw === null || Math.abs(this.lastCw - p.cwKgPerM) > 0.005) {
      this.lastCw = p.cwKgPerM;
      void q.enqueue('wind', frame(encodeWindResistance(p.cwKgPerM, p.windMps * 3.6, 1)), {
        responseKey: FEC_PAGE.WIND_RESISTANCE,
      });
    }
    await q.enqueue('track', frame(encodeTrackResistance(p.gradePct, p.crr)), {
      responseKey: FEC_PAGE.TRACK_RESISTANCE,
      minIntervalMs: SIM_MIN_INTERVAL_MS,
    });
  }

  private onMessage(dv: DataView): void {
    const msg = unframe(asBytes(dv));
    if (!msg) return;
    if (!msg.checksumOk || msg.page.length !== 8) {
      this.log('rx', dv, 'dropped: bad checksum or length');
      return;
    }
    const data = parsePage(msg.page);
    if (data.page === 25) {
      const d = data as ReturnType<typeof parsePage> & { powerW?: number; cadenceRpm?: number };
      const patch: { powerW?: number; cadenceRpm?: number } = {};
      if (d.powerW !== undefined) patch.powerW = d.powerW;
      if (d.cadenceRpm !== undefined) patch.cadenceRpm = d.cadenceRpm;
      this.emitData(patch);
    } else if (data.page === 16) {
      const d = data as { speedKmh: number; hrBpm?: number };
      this.emitData(d.hrBpm ? { speedKmh: d.speedKmh, hrBpm: d.hrBpm } : { speedKmh: d.speedKmh });
    } else if (data.page === 71) {
      const d = data as { lastCommandPage: number; status: number };
      this.log('rx', dv, `command status page ${d.lastCommandPage} status ${d.status}`);
      this.queue?.handleResponse(d.lastCommandPage, d.status === 0 || d.status === 0xff);
    } else if (this.logEmitter.size > 0) {
      this.log('rx', dv, `page ${data.page} ${toHex(msg.page)}`);
    }
  }
}
